import { inject, Injectable } from '@angular/core'
import { MeUser, UsersService } from './users.service'
import { LoggerService } from './logger.service'
import { environment } from '../environments/environment'
import { constructNameForUser } from './utils'
import Intercom, { showArticle } from '@intercom/messenger-js-sdk'
import { queryKeys } from './query-keys'
import { HttpClient } from '@angular/common/http'
import { injectMutation, injectQuery } from '@ngneat/query'
import { Organization } from './organizations.service'

@Injectable({
  providedIn: 'root',
})
export class IntercomService {
  usersService = inject(UsersService)
  loggerService = inject(LoggerService)
  httpClient = inject(HttpClient)
  query = injectQuery()
  mutation = injectMutation()

  initialize() {
    const user = this.usersService.currentUser$.value
    if (user !== null && user.organization.isIntercomEnabled && user.intercomHash !== null && user.intercomAppId !== null && environment.production) {
      Intercom({
        region: 'eu',
        app_id: user.intercomAppId,
        user_id: `${user.environment}-${user.id}`,
        name: constructNameForUser(user),
        email: user.email,
        company: user.organization.name,
        ...this.buildAttributes(user),
      })
      this.loggerService.info('Intercom initialized with organization attributes.')
    }
  }

  private buildAttributes(user: MeUser): Record<string, unknown> {
    const org = user.organization
    return {
      'User Role': user.role,
      'Organisation Name': org.name,
      'Retention Period Days': org.retentionPeriodInDaysForCall?.toString() ?? 'Unlimited',
      'Soft Deletion Period Days': org.softDeletionPeriodInDays,
      'Uploads Enabled': org.allowCallUpload,
      'Sharing Enabled': org.isSharingEnabled,
      'Calendar Sync Enabled': org.isCalendarSynchronizationEnabled,
      'Manual Meeting Creation Enabled': org.allowManualCallCreation,
      'Video Download Enabled': org.allowVideoDownload,
      'Transcript Download Enabled': org.allowTranscriptDownload,
      'Minutes Download Enabled': org.allowMinutesDownload,
      'Outline Download Enabled': org.allowOutlineDownload,
      'Input Language': org.transcriberInputLanguage,
    }
  }

  getAdmins() {
    return this.query({
      queryKey: queryKeys.intercom.admins,
      queryFn: () => this.httpClient.get<any[]>('/api/intercom/admins'),
    }).result$
  }

  startConversationMutation() {
    return this.mutation({
      mutationFn: (data: { adminId: string; message: string; userId: number }) => this.httpClient.post<void>(`api/intercom/start-conversation`, data),
    })
  }

  showArticle(articleId: string) {
    showArticle(articleId)
  }
}
